/**
 * Helpers for persisting a route in localStorage together with the moment it was
 * written, using the `timestamp:value` syntax (e.g. "1720915200000:/dashboard").
 *
 * The timestamp lets the login flow decide which of several saved routes is the
 * most recent, so the user is returned to the page they last touched.
 */

export type TimestampedRoute = {
  timestamp: number;
  value: string;
};

/**
 * Persists `value` to `key` prefixed with the current timestamp.
 * @param {string} key - localStorage key
 * @param {string} value - route/URL to store
 */
export const setTimestampedRoute = (key: string, value: string): void => {
  localStorage.setItem(key, `${Date.now()}:${value}`);
};

/**
 * Reads and parses a `timestamp:value` entry.
 * @param {string} key - localStorage key
 * @returns {TimestampedRoute | null} - parsed entry, or `null` when absent/malformed
 */
export const readTimestampedRoute = (key: string): TimestampedRoute | null => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  // Split on the first colon only: the value itself (a URL) may contain colons.
  const separator = raw.indexOf(":");
  if (separator === -1) return null;

  const timestamp = Number(raw.slice(0, separator));
  const value = raw.slice(separator + 1);
  if (!Number.isFinite(timestamp) || !value) return null;

  return { timestamp, value };
};

/**
 * Picks the route with the most recent timestamp among the given entries.
 * @param {...(TimestampedRoute | null)} entries - candidate entries
 * @returns {string | undefined} - the freshest route value, or `undefined` when none exist
 */
export const pickMostRecentRoute = (...entries: (TimestampedRoute | null)[]): string | undefined => {
  const latest = entries.filter((entry): entry is TimestampedRoute => entry !== null).sort((a, b) => b.timestamp - a.timestamp)[0];
  return latest?.value;
};
